import React, { Fragment, useRef, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiMenu } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { MdArrowForwardIos } from "react-icons/md";
import Drawer from "../../components/Drawer";
import BrandLogo from "../BrandLogo/BrandLogo";
import NavMiniCar from "./NavMiniCar";
import UserAccountMenu from "../UserAccountMenu";
import AccountQuickModal from "../AccountQuickModal";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { LOG_OUT } from "../../constant";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { scrollToHash } from "../../utils/window";
import { isDealerUser } from "../../utils/userTypes";
import { hasDealerPortalAccess } from "../../utils/dealerSessionSync";

const CLICK_GUARD_MS = 400;
let clickGuardTimer = null;

function armClickGuard() {
  if (typeof document === "undefined") return;
  document.body.classList.add("pi-click-guard");
  if (clickGuardTimer) {
    window.clearTimeout(clickGuardTimer);
  }
  clickGuardTimer = window.setTimeout(() => {
    document.body.classList.remove("pi-click-guard");
    clickGuardTimer = null;
  }, CLICK_GUARD_MS);
}

const NavLinkRoad = ({ title }) => (
  <span className="pi-header__link-stack">
    <span className="pi-header__link-text">{title}</span>
    <span className="pi-header__road" aria-hidden>
      <span className="pi-header__road-surface" />
      <span className="pi-header__road-dash" />
      <span className="pi-header__road-car">
        <NavMiniCar className="pi-header__road-car-svg" />
      </span>
    </span>
  </span>
);

const CurveNavbar = ({ setnavbarDimensions, auth, logOut }) => {
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const [accountPanel, setAccountPanel] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navbarContainer = useRef(null);
  const { feedback } = useContext(FeedbackContext);
  const home_page = feedback?.home_page || {};
  const history = useHistory();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isLoggedIn = Boolean(auth?.token);
  const showDealersEntry =
    isDealerUser(auth) || hasDealerPortalAccess(auth);

  const navLinks = [
    { id: "home", title: home_page.Home, to: "/", exact: true },
    { id: "sell", title: home_page.Sell_Any_Wheel, to: "/#sell-buy", isAnchor: true, hash: "#sell-buy" },
    { id: "how", title: home_page.How_Works, to: "/#how_work", isAnchor: true, hash: "#how_work" },
    { id: "cars", title: home_page.Cars?.trim() || "Cars", to: "/cars" },
    { id: "appointment", title: home_page.My_Appointment, to: "/myappointment" },
    { id: "contact", title: home_page.Contact_Us, to: "/contactus" },
    { id: "about", title: home_page.About_Us, to: "/aboutus" },
    { id: "program", title: "Credits", to: "/dostprogram" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const blurHeaderNavFocus = () => {
    const header = navbarContainer.current;
    const active = document.activeElement;
    if (header && active && header.contains(active)) {
      active.blur();
    }
  };

  useEffect(() => {
    if (isHome) return undefined;
    blurHeaderNavFocus();
  }, [location.pathname, isHome]);

  useEffect(() => {
    if (isHome) return undefined;

    const onKeyDown = (event) => {
      const scrollKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
      ];
      if (!scrollKeys.includes(event.key)) return;
      blurHeaderNavFocus();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isHome]);

  useEffect(() => {
    function setnavbarDimensionsHandler() {
      if (!navbarContainer.current) return;
      if (navbarContainer.current.offsetWidth > 1100) {
        setMobileDrawer(false);
      }
      setnavbarDimensions({
        width: navbarContainer.current.offsetWidth,
        height: navbarContainer.current.offsetHeight,
      });
    }
    setnavbarDimensionsHandler();
    window.addEventListener("resize", setnavbarDimensionsHandler);
    return () => window.removeEventListener("resize", setnavbarDimensionsHandler);
  }, [setnavbarDimensions]);

  useEffect(() => {
    setMobileDrawer(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!mobileDrawer) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileDrawer]);

  useEffect(() => {
    if (!mobileDrawer) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileDrawer]);

  const isActive = (item) => {
    if (item.isAnchor) {
      return location.pathname === "/" && location.hash === item.hash;
    }
    if (item.id === "home") {
      return location.pathname === "/" && !location.hash;
    }
    if (item.exact) {
      return location.pathname === item.to;
    }
    return location.pathname.startsWith(item.to);
  };

  const handleAnchorNav = (event, item) => {
    event.preventDefault();
    event.currentTarget.blur();

    const hashId = item.hash.replace(/^#/, "");

    if (location.pathname === "/" && location.hash === item.hash) {
      scrollToHash(item.hash);
      return;
    }

    history.push({ pathname: "/", hash: hashId });
  };

  const handleHomeNav = () => {
    if (location.pathname === "/" && location.hash) {
      history.replace("/");
    }
  };

  const handleDrawer = () => {
    setMobileDrawer((open) => {
      const next = !open;
      if (!next) armClickGuard();
      return next;
    });
  };
  const closeDrawer = () => {
    setMobileDrawer(false);
    armClickGuard();
  };
  const goHome = () => history.push("/");

  const headerClass = [
    "pi-header",
    isHome ? "pi-header--hero" : "pi-header--inner",
    scrolled ? "pi-header--scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const logoVariant = isHome ? "light" : "dark";
  const linkClass = (active) => `pi-header__link${active ? " is-active" : ""}`;

  return (
    <>
      <header id="curve-nav-bar-main-cont" className={headerClass} ref={navbarContainer}>
        <div className="pi-header__sheen" aria-hidden />
        <div className="pi-header__inner">
          <BrandLogo variant={logoVariant} compact onClick={goHome} className="pi-header__logo" />

          <nav className="pi-header__nav" aria-label="Main navigation">
            {navLinks.map((item) => {
              const active = isActive(item);
              const content = <NavLinkRoad title={item.title} />;
              if (item.isAnchor) {
                return (
                  <a
                    key={item.id}
                    href={item.to}
                    onClick={(event) => handleAnchorNav(event, item)}
                    className={linkClass(active)}
                    aria-current={active ? "page" : undefined}
                  >
                    {content}
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.id}
                  exact={item.exact}
                  to={item.to}
                  onClick={(event) => {
                    if (item.id === "home") handleHomeNav();
                    event.currentTarget.blur();
                  }}
                  className={linkClass(isActive(item))}
                  activeClassName="is-active"
                  isActive={() => isActive(item)}
                >
                  {content}
                </NavLink>
              );
            })}
          </nav>

          <div className="pi-header__actions">
            {showDealersEntry ? (
              <NavLink className="pi-header__cta" to="/dealers">
                {home_page.For_Dealers?.trim() || "For Dealers"}
              </NavLink>
            ) : null}
            {isLoggedIn ? (
              <UserAccountMenu />
            ) : (
              <NavLink className="pi-header__auth-link" to="/login">
                Login
              </NavLink>
            )}
            <button
              type="button"
              onClick={handleDrawer}
              className="pi-header__menu-btn"
              aria-label={mobileDrawer ? "Close menu" : "Open menu"}
              aria-expanded={mobileDrawer}
              aria-controls="pi-mobile-nav"
            >
              <FiMenu size={22} />
            </button>
          </div>
        </div>
      </header>

      <Drawer
        visible={mobileDrawer}
        handleDrawer={closeDrawer}
        body={
          <MobileDrawerBody
            handleDrawer={closeDrawer}
            home_page={home_page}
            navLinks={navLinks}
            goHome={goHome}
            isActive={isActive}
            onAnchorNav={handleAnchorNav}
            onHomeNav={handleHomeNav}
            isLoggedIn={isLoggedIn}
            showDealersEntry={showDealersEntry}
            onOpenAccount={(panel) => {
              closeDrawer();
              setAccountPanel(panel);
            }}
            onLogout={() => {
              closeDrawer();
              logOut();
              history.push("/");
            }}
          />
        }
      />

      <AccountQuickModal
        open={Boolean(accountPanel)}
        panel={accountPanel || "profile"}
        onClose={() => setAccountPanel(null)}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});
const mapDispatchToProps = (dispatch) => ({
  logOut: () => dispatch({ type: LOG_OUT }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CurveNavbar);

const ACCOUNT_DRAWER_LINKS = [
  { panel: "profile", label: "Edit profile" },
  { panel: "credits", label: "Credits & evaluations" },
  { panel: "password", label: "Change password" },
  { panel: "referral", label: "Invite friends" },
];

const DEALER_DRAWER_LINKS = [
  { to: "/dealers/dashbord", label: "Dealer dashboard" },
  { to: "/dealers/dashbord/auction", label: "Auctions" },
  { to: "/dealers/dashbord/buynow", label: "Buy now" },
];

const MobileDrawerBody = ({
  handleDrawer,
  home_page,
  navLinks,
  goHome,
  isActive,
  onAnchorNav,
  onHomeNav,
  isLoggedIn,
  showDealersEntry,
  onOpenAccount,
  onLogout,
}) => (
  <Fragment>
    <div className="pi-drawer__head">
      <BrandLogo variant="dark" onClick={() => { goHome(); handleDrawer(); }} />
      <button type="button" onClick={handleDrawer} className="pi-drawer__close" aria-label="Close menu">
        <AiOutlineClose size={22} />
      </button>
    </div>
    <nav className="pi-drawer__nav" id="pi-mobile-nav" aria-label="Mobile navigation">
      {navLinks.map((item) => {
        const active = isActive(item);
        const content = (
          <>
            <span className="pi-drawer__link-text">{item.title}</span>
            <MdArrowForwardIos size={14} className="pi-drawer__link-chevron" aria-hidden />
          </>
        );
        if (item.isAnchor) {
          return (
            <a
              key={item.id}
              href={item.to}
              onClick={(event) => {
                onAnchorNav(event, item);
                handleDrawer();
              }}
              className={`pi-drawer__link${active ? " is-active" : ""}`}
              aria-current={active ? "page" : undefined}
            >
              {content}
            </a>
          );
        }
        return (
          <NavLink
            key={item.id}
            onClick={() => {
              if (item.id === "home") onHomeNav();
              handleDrawer();
            }}
            exact={item.exact}
            to={item.to}
            className={`pi-drawer__link${active ? " is-active" : ""}`}
            isActive={() => isActive(item)}
            aria-current={active ? "page" : undefined}
          >
            {content}
          </NavLink>
        );
      })}

      {isLoggedIn ? (
        <>
          {showDealersEntry
            ? DEALER_DRAWER_LINKS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="pi-drawer__link"
                  onClick={handleDrawer}
                >
                  <span className="pi-drawer__link-text">{item.label}</span>
                  <MdArrowForwardIos
                    size={14}
                    className="pi-drawer__link-chevron"
                    aria-hidden
                  />
                </NavLink>
              ))
            : null}
          {ACCOUNT_DRAWER_LINKS.map((item) => (
            <button
              key={item.panel}
              type="button"
              className="pi-drawer__link"
              onClick={() => onOpenAccount?.(item.panel)}
            >
              <span className="pi-drawer__link-text">{item.label}</span>
              <MdArrowForwardIos size={14} className="pi-drawer__link-chevron" aria-hidden />
            </button>
          ))}
          <button
            type="button"
            className="pi-drawer__link pi-drawer__link--logout"
            onClick={onLogout}
          >
            <span className="pi-drawer__link-text">Log out</span>
          </button>
        </>
      ) : (
        <>
          <NavLink to="/login" className="pi-drawer__link" onClick={handleDrawer}>
            <span className="pi-drawer__link-text">Login</span>
            <MdArrowForwardIos size={14} className="pi-drawer__link-chevron" aria-hidden />
          </NavLink>
          <NavLink to="/signup" className="pi-drawer__link" onClick={handleDrawer}>
            <span className="pi-drawer__link-text">Sign up</span>
            <MdArrowForwardIos size={14} className="pi-drawer__link-chevron" aria-hidden />
          </NavLink>
        </>
      )}

      {showDealersEntry ? (
        <NavLink
          className="pi-drawer__link pi-drawer__link--cta"
          to="/dealers"
          onClick={handleDrawer}
        >
          <span>{home_page.For_Dealers?.trim() || "For Dealers"}</span>
          <MdArrowForwardIos size={14} aria-hidden />
        </NavLink>
      ) : null}
    </nav>
  </Fragment>
);
